import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { useToast } from '@/components/common/Toast';
import { useLoadingCoordinator } from '@/hooks/useLoadingCoordinator';
import { termsPolicyService } from '@/services/termsPolicy';
import type { TermsPolicy } from '@/types';

// Desktop components following SRP
import { PolicyHeader } from '@/components/desktop/policy/PolicyHeader';
import { PolicyContent } from '@/components/desktop/policy/PolicyContent';

export function DesktopTermsPolicyDetailPage() {
  const { id, title } = useParams<{ id?: string; title?: string }>();
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  
  // 모바일 로직 재사용 - 상태 관리
  const [termsPolicy, setTermsPolicy] = useState<TermsPolicy | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(true);

  const { 
    shouldShowLoading, 
    shouldShowSkeleton, 
    shouldShowContent 
  } = useLoadingCoordinator(isDataLoading, {
    useSkeletonMode: true,
    minLoadingTime: 200
  });

  useEffect(() => {
    const fetchTermsPolicy = async () => {
      if (!id && !title) {
        showToast('잘못된 접근입니다');
        navigate(-1);
        return;
      }

      try {
        setIsDataLoading(true);
        let result;
        
        if (title) {
          // 제목으로 조회 (URL 디코딩 필요)
          const decodedTitle = decodeURIComponent(title);
          result = await termsPolicyService.getTermsPolicyByTitle(decodedTitle);
        } else if (id) {
          // ID로 조회 (기존 방식)
          result = await termsPolicyService.getTermsPolicyById(id);
        }
        
        if (result?.success && result.data) {
          setTermsPolicy(result.data);
        } else {
          showToast(result?.error || '약관 및 정책을 불러오는데 실패했습니다');
          navigate(-1);
        }
      } catch (error) {
        console.error('Error fetching terms policy:', error);
        showToast('약관 및 정책을 불러오는데 실패했습니다');
        navigate(-1);
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchTermsPolicy();
  }, [id, title, showToast, navigate]);

  // 전체 화면 로딩 (인증 로딩)
  if (shouldShowLoading) {
    return (
      <DesktopLayout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">문서를 불러오는 중...</p>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  // 스켈레톤 로딩 (데이터 로딩)
  if (shouldShowSkeleton) {
    return (
      <DesktopLayout>
        <div className="min-h-[80vh] py-12">
          <div className="max-w-6xl mx-auto px-6">
            <PolicyHeader termsPolicy={null} className="mb-8" />
            <PolicyContent termsPolicy={null} />
          </div>
        </div>
      </DesktopLayout>
    );
  }

  // 메인 콘텐츠
  if (shouldShowContent) {
    return (
      <DesktopLayout>
        <div className="min-h-[80vh] py-12">
          <div className="max-w-6xl mx-auto px-6">
            
            {/* Header Section - SRP: 약관 제목과 메타 정보만 담당 */}
            <PolicyHeader 
              termsPolicy={termsPolicy}
              className="mb-8"
            />
            
            {/* Content Section - SRP: 약관 내용 표시만 담당 */}
            <PolicyContent 
              termsPolicy={termsPolicy}
            />

            {/* 뒤로가기 버튼 */}
            <div className="flex justify-center mt-8 pt-6">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 text-[14px] bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                이전 페이지로
              </button>
            </div>
          </div>
        </div>

        <ToastContainer />
      </DesktopLayout>
    );
  }

  return null;
}